/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-mixed-operators */
import React, { useRef, useState, useEffect, ReactNode } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { Button, ModalDialog, Loader, Icon, classnames } from "@culturehq/components";

import { useConfirmClose, useDoor, useIsMobile } from "app/hooks";
import { t, Trans } from "app/utils";
import { RECORDER_CUSTOMER_ID } from "app/utils/constants";
import { SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";
import { HandleMediaChange } from "components/stories/post-stories-v2/types";
import LazyInputRange from "components/survey/item-types/LazyInputRange";
import { Organization } from "chq";
import Timer from "./Timer";
import Prompter from "./Prompter/Prompter";
import ChqModalDialog from "./ChqModalDialog";
import MicrophoneIcon from "./icons/MicrophoneIcon";
import MonitorIcon from "./icons/MonitorIcon";
import MiniDisplayIcon from "./icons/MiniDisplayIcon";
import EffectsStarsIcon from "./icons/EffectsStarsIcon";
import PrompterIcon from "./icons/PrompterIcon";
import BackgroundsIcon from "./icons/BackgroundsIcon";
import LayoutBLIcon from "./icons/LayoutBLIcon";
import LayoutBrIcon from "./icons/LayoutBrIcon";
import effectsItem from "./effects-items.json";
import PaintBrushIcon from "./icons/PaintBrushIcon";
import HDIcon from "./icons/HDIcon";
import ZoomInIcon from "./icons/ZoomInIcon";
import BucketIcon from "./icons/BucketIcon";
import SunIcon from "./icons/SunIcon";
import LayoutCenterIcon from "./icons/LayoutCenterIcon";
import CameraRecorderEffects from "./CameraRecorderEffects";
import Carousel from "./Carousel";
import GetVideoThumbnail from "./GetVideoThumbnail";

const INITIAL_COUNTDOWN_VALUE = 3;

const PERMISSION_ERROR = "permission_denied";

declare global {
  interface Window {
    tsvb: any;
  }
}

interface EffectsSDKCameraRecorderProps {
  organization: Organization;
  className?: string;
  initialOpen?: boolean;
  onVideoRecorded: (file: HandleMediaChange) => Promise<void>;
  script?: string;
  children?: ReactNode;
  onOpen?: () => void;
}

const EffectsSDKCameraRecorder: React.FC<EffectsSDKCameraRecorderProps> = ({
  organization,
  children,
  className,
  initialOpen = false,
  script = undefined,
  onVideoRecorded,
  onOpen: onParentOpen
}) => {
  const { open, onOpen, onClose } = useDoor(initialOpen);
  const [audioDevices, setAudioDevices] = useState<{ label: any; value: any }[]>([]);
  const [videoDevices, setVideoDevices] = useState<{ label: any; value: any }[]>([]);
  const [askingPermission, setAskingPermission] = useState(false);
  const [videoConstraints, setVideoConstraints] = useState<any>(false);
  const [audioConstraints, setAudioConstraints] = useState<any>(false);
  const [videoEditorOpen, setVideoEditorOpen] = useState(false);
  const [screenRecord, setScreenRecord] = useState(false);
  const [manualStatus, setManualStatus] = useState<"recording" | "stopped" | "paused" | "idle" >("idle");
  const [persmissionRequested, setPermissionsRequested] = useState(false);
  const videoRef = useRef<any>(null);
  const [selectedAudioDeviceState, setSelectedAudioDeviceState] = useState<any>();
  const [selectedVideoDeviceState, setSelectedVideoDeviceState] = useState<any>();
  const [showScript, setShowScript] = useState(false);
  const [previewScript, setPreviewScript] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(undefined);
  const [recordEffects, setRecordEffects] = useState(false);
  const [mediaBlob, setMediaBlob] = useState<string | undefined>(undefined);
  const [effectsIsReady, setEffectsIsReady] = useState(false);
  const [backgrounds, setBackgrounds] = useState<any[]>(effectsItem.background);
  const [editableScript, setEditableScript] = useState(script || "");
  const hasCamera = !!videoDevices.length;

  const [countdown, setCountdown] = useState<number | undefined>();
  const [showCountdown, setShowCountdown] = useState(false);
  const [prompterWasOpen, setPrompterWasOpen] = useState(false);
  const [retake, setRetake] = useState(false);

  const recorderPreview = useReactMediaRecorder({
    screen: screenRecord,
    video: videoConstraints,
    audio: audioConstraints
  });

  const {
    status,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    previewStream,
    previewAudioStream,
    mediaBlobUrl,
    clearBlobUrl,
    error
  } = recorderPreview;
  const [effectsSDK, setEffectsSDK] = useState<any>(null);
  const [currentOption, setCurrentOption] = useState<"none" | "video" | "audio" | "screen" | "backgrounds" | "layout" | "effects" | "prompter">("none");

  const [layoutSelection, setLayoutSelection] = useState<"center" | "left-bottom" | "right-bottom">("center");
  const [backgroundSelection, setBackgroundSelection] = useState("None");
  const backgroundFileInputRef = useRef<HTMLInputElement>(null);

  const isMobile = useIsMobile();

  // effects
  const [videoQuality, setVideoQuality] = useState(0);
  const [retouch, setRetouch] = useState(0);
  const [colorFilter, setColorFilter] = useState(0);
  const [lowLight, setLowLight] = useState(0);
  const [smartZoom, setSmartZoom] = useState(0);
  const [blur, setBlur] = useState(0);
  const [selectedEffects, setSelectedEffects] = useState< number[]>([]);

  const effectsStates = [
    videoQuality,
    retouch,
    colorFilter,
    lowLight,
    smartZoom
  ];

  const effectsSetters = [
    setVideoQuality,
    setRetouch,
    setColorFilter,
    setLowLight,
    setSmartZoom
  ];

  const changeIntercomDisplay = (hide: boolean) => {
    const intercomElement = document.getElementsByClassName("intercom-lightweight-app")[0] as HTMLElement;
    if (intercomElement?.style) {
      intercomElement.style.display = hide ? "none" : "block";
    }
  };


  // hide the intercom button
  useEffect(() => {
    if (open && isMobile) {
      changeIntercomDisplay(true);
    }

    return () => {
      changeIntercomDisplay(false);
    };
  }, [isMobile, open]);

  useEffect(() => {
    if (showScript && !screenRecord && countdown !== undefined) {
      setShowScript(false);
      setPrompterWasOpen(true);
    }
    setCurrentOption("none");
    const isCountdownChecked = (countdown || 0) > 0 && (countdown || 0) <= INITIAL_COUNTDOWN_VALUE;
    const timeoutId = isCountdownChecked
      ? setTimeout(() => setCountdown((countdown || 0) - 1), 1000) : null;
    if (countdown === 0) {
      handleStartRecord();
      setCountdown(undefined);
      setShowCountdown(false);
      if (prompterWasOpen) {
        setShowScript(true);
        setPrompterWasOpen(false);
      }
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  useEffect(() => {
    if (!open || !onParentOpen) return;

    onParentOpen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!organization.virtualBackgrounds?.length) return;

    const noneAndBlur = defaultBackgrounds();
    setBackgrounds([
      ...noneAndBlur,
      ...organization.virtualBackgrounds.map((vbg: any) => ({
        media: vbg.fullUrl || vbg.url,
        thumbnail: vbg.thumbnail || vbg.smallUrl || vbg.url,
        video: vbg.mediaType === "video",
        name: vbg.url.split("/").pop(),
        localFile: false,
        displayName: false
      }))]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organization.virtualBackgrounds]);

  useEffect(() => {
    // eslint-disable-next-line new-cap
    setEffectsSDK(new window.tsvb(RECORDER_CUSTOMER_ID));
  }, []);

  useEffect(() => {
    if (error === PERMISSION_ERROR && !screenRecord) {
      onCloseModal();
    }

    if (error === PERMISSION_ERROR && screenRecord) {
      setVideoConstraints({ width: window.screen.width, height: window.screen.height });
      setScreenRecord(false);
      setManualStatus("idle");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (effectsSDK) {
      effectsSDK.config({
        provider: "webgpu"
      });
      effectsSDK.onReady = () => {
        setEffectsIsReady(true);
        effectsSDK.run();
        if (screenRecord) {
          effectsSDK.disableMirroring();
        } else {
          effectsSDK.enableMirroring();
        }
      };
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [effectsSDK, manualStatus]);

  useEffect(() => {
    if (!askingPermission && status === "acquiring_media" && !persmissionRequested) {
      setAskingPermission(true);
    }

    if (askingPermission && status === "recording" && !persmissionRequested) {
      setPermissionsRequested(true);
      getDevices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askingPermission, open, persmissionRequested, status]);

  useEffect(() => {
    const onEndedEventListener = () => {
      stopRecording();
      setMediaBlob(mediaBlobUrl);
      setTimeout(() => {
        setVideoEditorOpen(true);
      }, 1000);
    };
    const isVideoTrackRecording = screenRecord && status === "recording";
    const videoTrack = isVideoTrackRecording && previewStream?.getVideoTracks()?.[0];
    if (videoTrack) videoTrack.addEventListener("ended", onEndedEventListener);

    return () => {
      if (videoTrack) videoTrack.removeEventListener("ended", onEndedEventListener);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenRecord, status]);

  useEffect(() => {
    if (!hasCamera || audioDevices.length <= 0) getDevices();
    if (open) {
      stopRecording();
      clearBlobUrl();
      startRecording();

      if (screenRecord) {
        setManualStatus("recording");
        setCurrentOption("none");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenRecord, open]);

  useEffect(() => {
    if (videoRef.current && previewStream && effectsSDK && hasCamera) {
      if (!videoRef.current.srcObject) {
        if (!retake) {
          effectsSDK.clear();
          effectsSDK.useStream(previewStream);
        }

        videoRef.current.srcObject = effectsSDK.getStream();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectsSDK, previewStream, hasCamera]);

  useEffect(() => {
    if (open && videoConstraints.deviceId && audioConstraints.deviceId) {
      startRecording();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoConstraints.deviceId, audioConstraints.deviceId]);

  useEffect(() => {
    let isMounted = true;
    const getDevicesByKind = (devices: any[], kind: string) => devices
      .filter((device: { kind: any }) => device.kind === kind)
      .map(({ label, deviceId }) => ({ label, value: deviceId }));

    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (!isMounted) return;
      setAudioDevices(getDevicesByKind(devices, "audioinput"));
      setVideoDevices(getDevicesByKind(devices, "videoinput"));
    };

    getDevices();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setVideoConstraints(
      hasCamera && {
        deviceId: videoDevices[0].value,
        width: { max: 1920, min: 1080 },
        height: { max: 1080, min: 720 }
      }
    );
    setSelectedVideoDeviceState(videoDevices[0]?.value);
  }, [videoDevices, hasCamera]);

  useEffect(() => {
    setAudioConstraints(
      audioDevices.length > 0 && { deviceId: audioDevices[0].value }
    );
    setSelectedAudioDeviceState(audioDevices[0]?.value);
  }, [audioDevices]);

  const defaultBackgrounds = () => backgrounds.filter(bg => bg.name === "None" || bg.name === "Blur");

  const selectRecordMethod = (value: string | SelectValueWithCategory | null) => {
    if (isDisableOption("screen")) return;

    setVideoConstraints({ width: window.screen.width, height: window.screen.height });
    setScreenRecord(value === "screen");
  };

  const handleSelectedAudioDevice = (value: string | SelectValueWithCategory | null) => {
    if (value === selectedAudioDeviceState) return;

    setAudioConstraints({ deviceId: value });
    setSelectedAudioDeviceState(value);
  };

  const handleSelectedVideoDevice = (value: string | SelectValueWithCategory | null) => {
    if (value === selectedVideoDeviceState) return;

    stopRecording();
    setVideoConstraints({
      deviceId: value,
      width: { max: 1920, min: 1080 },
      height: { max: 1080, min: 720 }
    });
    setSelectedVideoDeviceState(value);
  };

  const onCloseModal = () => {
    onClose();
    resetState();
    stopRecording();
  };

  const { onConfirmClose } = useConfirmClose(
    t("You are about to close the recording modal. Any unsaved video will be lost. Are you sure you want to proceed?"),
    onCloseModal,
    true
  );

  const startCountdown = () => {
    setShowCountdown(true);
    setCountdown(INITIAL_COUNTDOWN_VALUE);
  };

  const handleStartRecord = () => {
    setCurrentOption("none");
    setRecordEffects(true);

    if (videoRef.current) {
      videoRef.current.srcObject = effectsSDK.getStream();
      setManualStatus("recording");
    }
  };

  const handleRetake = () => {
    setRecordEffects(false);
    setRetake(true);
    setManualStatus("idle");
    setShowCountdown(false);
  };

  const resetState = () => {
    setAudioDevices([]);
    setVideoDevices([]);
    setAskingPermission(false);
    setVideoConstraints(false);
    setAudioConstraints(false);
    setVideoEditorOpen(false);
    setScreenRecord(false);
    setManualStatus("idle");
    setPermissionsRequested(false);
    setSelectedAudioDeviceState(audioDevices[0]?.label);
    setSelectedVideoDeviceState(videoDevices[0]?.label);
    setShowScript(false);
    setPreviewScript(false);
    setSelectedFile(undefined);
    setRecordEffects(false);
    setMediaBlob(undefined);
    setCurrentOption("none");
    setLayoutSelection("center");
    setBackgroundSelection("None");
    setBackgrounds(effectsItem.background);

    // effects
    setVideoQuality(0);
    setRetouch(0);
    setColorFilter(0);
    setLowLight(0);
    setSmartZoom(0);
    setBlur(0);
    setSelectedEffects([]);
    setEffectsIsReady(false);
  };

  const finishRecord = () => {
    if (screenRecord) {
      stopRecording();
      setMediaBlob(mediaBlobUrl);
      setTimeout(() => {
        setVideoEditorOpen(true);
      }, 1000);
    }
    setManualStatus("stopped");
  };

  const handleVideoEdited = async (thumb: Blob) => {
    if (screenRecord && !mediaBlobUrl) return;
    if (!screenRecord && !mediaBlob) return;

    setVideoEditorOpen(false);
    const blobToUse = (screenRecord ? mediaBlobUrl : mediaBlob) || "";
    const videoBlob = await fetch(blobToUse).then(file => file.blob());
    const duration: number = await new Promise(resolve => {
      const video = document.createElement("video");

      video.onloadedmetadata = () => {
        if (video.duration === Infinity) {
          video.currentTime = Number.MAX_SAFE_INTEGER;
          video.ontimeupdate = () => {
            video.ontimeupdate = null;
            video.currentTime = 0;
            resolve(video.duration);
            URL.revokeObjectURL(video.src);
          };
        } else {
          resolve(video.duration);
          URL.revokeObjectURL(video.src);
        }
      };

      video.src = URL.createObjectURL(videoBlob);
    });
    onVideoRecorded({ media: videoBlob, thumb, isRecordedVideo: true, duration });
    onCloseModal();
    clearBlobUrl();
  };

  const handlePause = () => {
    if (script) {
      setPreviewScript(false);
    }
    setManualStatus("paused");
    pauseRecording();
  };

  const handleResume = () => {
    if (script) {
      setPreviewScript(false);
    }
    setManualStatus("recording");
    resumeRecording();
  };

  const handleSelectOption = (option: "none" | "video" | "audio" | "screen" | "backgrounds" | "layout" | "effects" | "prompter") => {
    const isDisable = isDisableOption(option);
    if (isDisable) {
      setCurrentOption("none");
      return;
    }
    if ((screenRecord && !(manualStatus === "recording")) || manualStatus === "recording") {
      if (screenRecord && !(manualStatus === "recording") && option === "screen") {
        setCurrentOption(prev => (prev !== "none" && prev !== "prompter" ? "none" : option));
      }

      if (manualStatus === "recording" && !screenRecord) {
        setCurrentOption(prev => (prev !== "none" && prev !== "prompter" ? "none" : option));
      }
    } else {
      setCurrentOption(prev => (prev !== "none" && prev !== "prompter" ? "none" : option));
    }
  };

  const isDisableOption = (option: string) => {
    if (option === "prompter") {
      return !effectsIsReady || isNotPrompter(currentOption);
    }
    if (screenRecord) {
      return true;
    }

    if ((option === "backgrounds" || option === "layout" || option === "effects") && !effectsIsReady) {
      return true;
    }

    if (option === "video" && !hasCamera) {
      return true;
    }

    return isNotPrompter(currentOption) && currentOption !== option;
  };

  const handleShowScriptChange = (value: string | SelectValueWithCategory | null) => {
    if (!effectsIsReady) return;
    setCurrentOption(prev => {
      const notPrompter = isNotPrompter(prev);
      setShowScript(notPrompter ? false : value === "yes");
      if (notPrompter) {
        return "none";
      }

      return (value === "yes" ? "prompter" : "none");
    });
  };

  const isNotPrompter = (value: string) => value !== "none" && value !== "prompter";

  const CloseIcon = () => <Icon className="icon-container__icon" icon="close" />;

  const isAudioDevice = (device: string) => {
    if (selectedAudioDeviceState) return selectedAudioDeviceState === device;

    return false;
  };

  const isVideoDevice = (device: string) => {
    if (selectedVideoDeviceState) {
      return selectedVideoDeviceState === device;
    }

    return false;
  };

  const getEffectIcon = (effectName: string) => {
    switch (effectName) {
      case "Video Quality":
        return <HDIcon className="icon" />;
      case "Retouch":
        return <PaintBrushIcon className="icon" />;
      case "Color Filter":
        return <BucketIcon className="icon" />;
      case "Low Light":
        return <SunIcon className="icon" />;
      default:
        return <ZoomInIcon className="icon" />;
    }
  };

  const handleSelectEffect = (item: any, index: number) => {
    const existingEffect = selectedEffects.findIndex(se => se === index);
    if (existingEffect !== -1) {
      // removing effect
      setSelectedEffects(selectedEffects.filter(se => se !== index));
      effectsSetters[index](0);
      manageEffect(index, false);
    } else {
      // adding effect
      setSelectedEffects([...selectedEffects, index]);
      effectsSetters[index](item.defaultValue);
      manageEffect(index, true, item.defaultValue);
    }
  };

  const manageEffect = async (index: number, enable: boolean, value?: number) => {
    switch (index) {
      // Video Quality
      case 0:
        if (effectsSDK) {
          if (enable) {
            effectsSDK.setSharpnessEffectConfig({ power: value ? value / 100 : 0.5 });
            effectsSDK.enableSharpnessEffect();
          } else {
            effectsSDK.disableSharpnessEffect();
          }
        }
        break;
      // Retouch
      case 1:
        if (enable) {
          effectsSDK.enableBeautification();
          effectsSDK.setBeautificationLevel(value ? value / 100 : 0.5);
        } else {
          effectsSDK.disableBeautification();
        }
        break;
      case 2:
        // Color Filter
        if (effectsSDK) {
          if (enable) {
            effectsSDK.enableColorCorrector();
            effectsSDK.setColorCorrectorPower(value ? value / 100 : 0.5);
          } else {
            effectsSDK.disableColorCorrector();
          }
        }
        break;
      // Low
      case 3:
        if (effectsSDK) {
          if (enable) {
            effectsSDK.enableLowLightEffect();
            effectsSDK.setLowLightEffectPower(value ? value / 100 : 0.5);
          } else {
            effectsSDK.disableLowLightEffect();
          }
        }
        break;
      // Smart Zoom
      case 4:
        if (effectsSDK) {
          if (enable) {
            effectsSDK.enableSmartZoom();
            effectsSDK.setFaceArea(value ? value / 100 : 0.5);
          } else {
            effectsSDK.disableSmartZoom();
          }
        }
        break;
      default:
        break;
    }
  };

  const handleRangeEffectChange = (index: number, range: number) => {
    const isActive = isActiveEffect(index) !== undefined;
    if (range === 0 && isActive) {
      setSelectedEffects(selectedEffects.filter(se => se !== index));
      manageEffect(index, false);
    }
    if (range !== 0 && !isActive) {
      setSelectedEffects([...selectedEffects, index]);
      manageEffect(index, true, range);
    }

    if (range !== 0 && isActive) {
      manageEffect(index, true, range);
    }
    effectsSetters[index](range);
  };

  const isActiveEffect = (index: number) => selectedEffects.find(se => se === index);

  const handleLayoutChange = (layout: "center" | "left-bottom" | "right-bottom") => {
    if (effectsSDK) {
      effectsSDK.setLayout(layout);
      setLayoutSelection(layout);
    }
  };

  const handleBackgroundChange = (backgroundItem: any) => {
    setBackgroundSelection(backgroundItem.name);
    switch (backgroundItem.name) {
      case "None":
        if (effectsSDK) {
          effectsSDK.clearBackground();
          effectsSDK.clearBlur();
          setBlur(0);
        }
        break;
      case "Blur":
        if (effectsSDK && blur === 0) {
          effectsSDK.clearBackground();
          setBlur(50);
          effectsSDK.setBlur(50 / 100);
        }
        break;
      default:
        if (effectsSDK) {
          effectsSDK.clearBlur();
          if (backgroundItem.localFile && backgroundItem.video) {
            const video = document.createElement("video");
            video.src = backgroundItem.media;
            video.addEventListener("loadeddata", () => {
              effectsSDK.setBackground(video);
              setSelectedFile(video);
            });
          } else {
            effectsSDK.setBackground(`${backgroundItem.media}?timestamp=${Date.now()}`);
          }
          setBlur(0);
        }
        break;
    }
  };

  const createVideoThumbnail = (videoFile: File): Promise<string> => new Promise(resolve => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    video.src = URL.createObjectURL(videoFile);
    video.load();

    video.addEventListener("loadeddata", () => {
      video.currentTime = 0.1;
      effectsSDK.setBackground(video);
      setSelectedFile(video);
    });

    video.addEventListener("seeked", () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // eslint-disable-next-line no-unused-expressions
      (ctx?.drawImage(video, 0, 0, canvas.width, canvas.height));

      const thumbnailUrl = canvas.toDataURL("image/jpeg");
      URL.revokeObjectURL(video.src);

      resolve(thumbnailUrl);
    });
  });

  const handleFileInputChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.files) {
      const file = Array.from(target.files)[0];
      const media = file;
      const mediaUrl = URL.createObjectURL(media);
      effectsSDK.clearBlur();
      setBackgroundSelection("Input");
      setBlur(0);

      const noneAndBlur = defaultBackgrounds();
      const otherBackgrounds = backgrounds.filter(bg => bg.name !== "None" && bg.name !== "Blur");

      if (file.type.startsWith("image/")) {
        effectsSDK.setBackground(mediaUrl);
        setSelectedFile(mediaUrl);

        setBackgrounds([
          ...noneAndBlur,
          {
            localFile: true,
            name: file.name,
            media: mediaUrl,
            displayName: false
          },
          ...otherBackgrounds
        ]);
      }

      if (file.type.startsWith("video/")) {
        const video = document.createElement("video");
        video.src = mediaUrl;
        const thumbnailUrl = await createVideoThumbnail(file);

        setBackgrounds([
          ...noneAndBlur,
          {
            localFile: true,
            name: file.name,
            media: mediaUrl,
            displayName: false,
            video: true,
            thumbnail: thumbnailUrl
          },
          ...otherBackgrounds
        ]);
      }
      setBackgroundSelection(file.name);
    }
  };

  const addAudioToTheTrack = () => {
    if (effectsSDK.getStream().getTracks().length === 1) {
      effectsSDK.getStream().addTrack(previewAudioStream?.getTracks()[0]);
    }
    return effectsSDK.getStream();
  };

  const getDevices = async () => {
    const getDevicesByKind = (devices: any[], kind: string) => devices
      .filter((device: { kind: any }) => device.kind === kind)
      .map(({ label, deviceId }) => ({ label, value: deviceId }));

    const devices = await navigator.mediaDevices.enumerateDevices();
    if (!open) return;
    setAudioDevices(getDevicesByKind(devices, "audioinput"));
    setVideoDevices(getDevicesByKind(devices, "videoinput"));
  };

  const handleFinishEffectsSDKRecord = (blobUrl: string) => {
    setMediaBlob(blobUrl);
    setTimeout(() => {
      setVideoEditorOpen(true);
    }, 1000);
  };

  return (
    <>
      {open && (
        <ChqModalDialog entrance="zoomIn" onClose={onConfirmClose} className="camera-recorder-modal camera-recorder-modal-full camera-recorder-modal-background" ariaLabelledBy="camera-recorder-title" ariaDescribedby="camera-recorder-title" style={{ overlay: { backgroundColor: "#000000" } }}>
          <ModalDialog.Body>
            <div className="Video-Preview__Container">
              {!videoEditorOpen && (status === "recording" || status === "paused") && (
                <>
                  <div className="Video-Preview__Container-Top">
                    {(recordEffects || screenRecord) && (manualStatus === "recording" || manualStatus === "paused") && <Timer pause={manualStatus !== "recording"} />}
                    <div className="Button-Container">
                      {manualStatus !== "stopped"
                      && (
                        <>
                          {!isMobile && (manualStatus === "recording" || manualStatus === "paused") && (
                            <Button
                              aria-label={t("Cancel record")}
                              inverted
                              disabled={!effectsIsReady}
                              icon="refresh"
                              onClick={handleRetake}
                            >
                              <Trans>Retake</Trans>
                            </Button>
                          )}
                          {(manualStatus === "recording" && !screenRecord && !isMobile) && (
                            <Button
                              aria-label={t("Pause record")}
                              onClick={handlePause}
                              icon="pause"
                              className="Button-Gray"
                            >
                              <Trans>Pause</Trans>
                            </Button>
                          )}
                          {(manualStatus === "paused" && !isMobile)
                            && (
                              <Button
                                aria-label={t("Resume record")}
                                onClick={handleResume}
                                icon="play-filled"
                                className="Button-Gray"
                              >
                                <Trans>Resume</Trans>
                              </Button>
                            )}
                          {(previewScript || (manualStatus !== "recording") && (manualStatus !== "paused") && !isMobile)
                            && (
                              <Button disabled={!effectsIsReady || showCountdown} icon="checkmark-filled" onClick={startCountdown}>
                                <Trans>Start Recording</Trans>
                              </Button>
                            )}
                          {((manualStatus === "recording" || manualStatus === "paused") && !isMobile)
                            && (
                              <Button
                                className="Button-Record"
                                aria-label={t("Finish record")}
                                onClick={finishRecord}
                                icon="checkmark-filled"
                              >
                                <Trans>Finish</Trans>
                              </Button>
                            )}
                          <Icon className="Video-Preview__Container-Top__icon" icon="close" onClick={onConfirmClose} />
                        </>
                      )}
                    </div>
                  </div>
                  {showCountdown && !showScript && (
                    <h1 className="Video-Top_Info-Effects">
                      <Trans>Recording in</Trans>
                      <br />
                      <span className="Video-Top_Info-Effects--Counter">{countdown}</span>
                    </h1>
                  )}
                  {recordEffects && (
                    <CameraRecorderEffects
                      customMediaStream={addAudioToTheTrack()}
                      videoConstraints={videoConstraints}
                      audioConstraints={audioConstraints}
                      status={manualStatus}
                      onFinishRecord={handleFinishEffectsSDKRecord}
                      onClick={() => { setCurrentOption("none"); }}
                    />
                  )}
                  {hasCamera && !recordEffects && (
                    <div className="Video-Preview__Source">
                      <video
                        muted
                        className={classnames("Video-Preview__Source-Video", { "Video-Preview__Source-Video__filtered": showCountdown })}
                        ref={videoRef}
                        width={1080}
                        autoPlay
                        onClick={() => { setCurrentOption("none"); }}
                      />
                      {!effectsIsReady && (
                        <div className="Video-Preview__Source-Overlay">
                          <Loader loading />
                        </div>
                      )}
                    </div>
                  )}
                  {!hasCamera && (
                    <div style={{ height: "400px", alignContent: "center", textAlign: "center" }}>
                      <h1>It looks like your camera is missing.</h1>
                      <h1>You can record your screen instead.</h1>
                    </div>
                  )}
                  <>
                    {showScript && !screenRecord && (
                      <Prompter editableScript={editableScript} setEditableScript={setEditableScript} script={script || ""} setShowScript={setShowScript} />
                    )}
                  </>
                  {manualStatus !== "stopped"
                  && (
                    <div className="Options-Container">
                      {!isMobile && (
                        <>
                          <div className={classnames("Options-Container__source", isDisableOption("video") ? "disabled" : "")}>
                            {currentOption === "video" && (
                              <div className="option-menu chq-ffd--sl--opts chq-ffd--sl--opts-open">
                                {videoDevices.map(videoDevice => (
                                  <button
                                    onClick={() => {
                                      handleSelectedVideoDevice(videoDevice.value);
                                    }}
                                    aria-current={isVideoDevice(videoDevice.value)}
                                    aria-label={`video device ${videoDevice.value}`}
                                    type="button"
                                    className="chq-pbn"
                                  >
                                    {videoDevice.label}
                                  </button>
                                )
                                )}
                              </div>
                            )}
                            <div className="option" onClick={() => handleSelectOption("video")}>
                              <div className="icon-container">
                                <Icon className="icon-container__icon" icon={currentOption === "video" ? "close" : "video-camera-filled-complete"} />
                              </div>
                              <span className="option-legend"><Trans>Video</Trans></span>
                            </div>
                            <div className="arrow" onClick={() => handleSelectOption("video")}>
                              <span aria-hidden="true" className={classnames("chq-dd--caret", { "chq-dd--caret-ex ": currentOption === "video" })} />
                            </div>
                          </div>
                          <div className={classnames("Options-Container__source", isDisableOption("audio") ? "disabled" : "")}>
                            {currentOption === "audio" && (
                              <div className="option-menu option-menu__audio chq-ffd--sl--opts chq-ffd--sl--opts-open">
                                {audioDevices.map(audioDevice => (
                                  <button
                                    onClick={() => {
                                      handleSelectedAudioDevice(audioDevice.value);
                                    }}
                                    aria-current={isAudioDevice(audioDevice.value)}
                                    aria-label={`audio device ${audioDevice.value}`}
                                    type="button"
                                    className="chq-pbn"
                                  >
                                    {audioDevice.label}
                                  </button>
                                )
                                )}
                              </div>
                            )}
                            <div className="option" onClick={() => handleSelectOption("audio")}>
                              <div className="icon-container">
                                {currentOption === "audio" ? <CloseIcon /> : <MicrophoneIcon className="icon-container__icon" />}
                              </div>
                              <span className="option-legend"><Trans>Audio</Trans></span>
                            </div>
                            <div className="arrow" onClick={() => handleSelectOption("audio")}>
                              <span aria-hidden="true" className={classnames("chq-dd--caret", { "chq-dd--caret-ex ": currentOption === "audio" })} />
                            </div>
                          </div>
                          <div className={classnames("Options-Container__source", isDisableOption("screen") ? "disabled" : "")}>
                            {currentOption === "screen" && (
                              <div className="option-menu option-menu__screen chq-ffd--sl--opts chq-ffd--sl--opts-open" />
                            )}
                            <div className="option" onClick={() => selectRecordMethod("screen")}>
                              <div className="icon-container">
                                {currentOption === "screen" ? <CloseIcon /> : <MonitorIcon className="icon-container__icon" />}
                              </div>
                              <span className="option-legend"><Trans>Screen</Trans></span>
                            </div>
                          </div>
                        </>
                      )}
                      <div className="vertical-divider" />
                      {
                        (!isMobile || (isMobile && (manualStatus !== "recording"))) && (
                          <>
                            <div className={classnames("Options-Container__effects", isDisableOption("backgrounds") ? "disabled" : "")}>
                              {currentOption === "backgrounds" && (
                                <div className="option-menu option-menu__background chq-ffd--sl--opts chq-ffd--sl--opts-open no-scrollbar">
                                  <Carousel data={backgrounds} elementWidth={107}>
                                    <div className="items-container">
                                      <div
                                        className={classnames("background-item", "invert-icon", backgroundSelection === "Input" ? "background-item__active" : "")}
                                        onClick={() => {
                                          if (backgroundFileInputRef.current) {
                                            backgroundFileInputRef.current.click();
                                          }
                                        }}
                                      >
                                        <input
                                          ref={backgroundFileInputRef}
                                          type="file"
                                          id="fileInput"
                                          onChange={handleFileInputChange}
                                          accept="image/*,video/*"
                                          style={{ display: "none" }}
                                        />
                                        <Icon icon="download" />
                                        <Trans>Upload</Trans>
                                      </div>
                                      <div className="vertical-divider" />
                                      {backgrounds.map((item, idx) => (
                                        <div
                                          key={idx}
                                          className={classnames("background-item", backgroundSelection === item.name ? "background-item__active" : "")}
                                          style={{
                                            backgroundImage: item.video && !item.thumbnail
                                              ? "none"
                                              : `url(${item.thumbnail || item.media})`,
                                            backgroundColor: "#f3f3f3"
                                          }}
                                          onClick={() => { handleBackgroundChange(item); }}
                                        >
                                          {item.displayName
                                          && <span><Trans>{item.name}</Trans></span>}
                                          {item.video && (
                                            <div className="video-indicator">
                                              <Icon icon="play-filled" />
                                            </div>
                                          )}
                                          {item.range && (
                                            <div className="range-bar">
                                              <LazyInputRange
                                                maxValue={100}
                                                minValue={0}
                                                value={blur}
                                                onChange={(range: any) => {
                                                  if (range > 0) {
                                                    setBackgroundSelection("Blur");
                                                    effectsSDK.clearBackground();
                                                    setBlur(range);
                                                    effectsSDK.setBlur(range / 100);
                                                  } else {
                                                    effectsSDK.clearBlur();
                                                    setBlur(0);
                                                    setBackgroundSelection("None");
                                                  }
                                                }}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </Carousel>
                                </div>
                              )}
                              <div className="option" onClick={() => handleSelectOption("backgrounds")}>
                                <div className="icon-container">
                                  {currentOption === "backgrounds" ? <CloseIcon /> : <BackgroundsIcon className="icon-container__icon" />}
                                </div>
                                <span className="option-legend"><Trans>Backgrounds</Trans></span>
                              </div>
                              <div className="arrow" onClick={() => handleSelectOption("backgrounds")}>
                                <span aria-hidden="true" className={classnames("chq-dd--caret video-effects", { "chq-dd--caret-ex ": currentOption === "backgrounds" })} />
                              </div>
                            </div>
                            <div className={classnames("Options-Container__effects", isDisableOption("layout") ? "disabled" : "")}>
                              {currentOption === "layout" && (
                                <div className="option-menu option-menu__layout chq-ffd--sl--opts chq-ffd--sl--opts-open">
                                  <div className="items-container">
                                    <div onClick={() => { handleLayoutChange("center"); }}>
                                      <LayoutCenterIcon className={classnames("item", layoutSelection === "center" ? "item__active" : "")} />
                                    </div>
                                    <div onClick={() => { handleLayoutChange("left-bottom"); }}>
                                      <LayoutBLIcon className={classnames("item", layoutSelection === "left-bottom" ? "item__active" : "")} />
                                    </div>
                                    <div onClick={() => { handleLayoutChange("right-bottom"); }}>
                                      <LayoutBrIcon className={classnames("item", layoutSelection === "right-bottom" ? "item__active" : "")} />
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div className="option" onClick={() => handleSelectOption("layout")}>
                                <div className="icon-container">
                                  {currentOption === "layout" ? <CloseIcon /> : <MiniDisplayIcon className="icon-container__icon" />}
                                </div>
                                <span className="option-legend"><Trans>Layout</Trans></span>
                              </div>
                              <div className="arrow" onClick={() => handleSelectOption("layout")}>
                                <span aria-hidden="true" className={classnames("chq-dd--caret video-effects", { "chq-dd--caret-ex ": currentOption === "layout" })} />
                              </div>
                            </div>
                          </>
                        )
                      }
                      {isMobile && (manualStatus === "recording") && (
                        <>
                          <div className="Mobile-Record-Button" onClick={handlePause}>
                            <Icon className="Mobile-Record-Button__icon" icon="pause" />
                          </div>
                          <div className="Mobile-Record-Button" onClick={finishRecord}>
                            <div className="finish-icon" />
                          </div>
                        </>
                      )}
                      {isMobile && (manualStatus === "idle") && (
                        <div className="Mobile-Record-Button" onClick={startCountdown}>
                          <Icon className="record-icon" icon="record" />
                        </div>
                      )}
                      {isMobile && (manualStatus === "paused") && (
                        <div className="Mobile-Record-Button" onClick={handleResume}>
                          <Icon className="record-icon" icon="record" />
                        </div>
                      )}
                      {(!isMobile || (isMobile && (manualStatus !== "recording"))) && (
                        <>
                          <div className={classnames("Options-Container__effects", isDisableOption("effects") ? "disabled" : "")}>
                            {currentOption === "effects" && (
                              <div className="option-menu option-menu__effects chq-ffd--sl--opts chq-ffd--sl--opts-open">
                                <div className="items-container">
                                  {effectsItem.effects.map((item, index) => (
                                    <div className="effects-item">
                                      <div onClick={() => handleSelectEffect(item, index)}>
                                        <Trans>{item.name}</Trans>
                                      </div>
                                      <div className={isActiveEffect(index) !== undefined ? "icon-container__active" : "icon-container"} onClick={() => handleSelectEffect(item, index)}>
                                        {getEffectIcon(item.name)}
                                      </div>
                                      <div className="range-bar">
                                        <LazyInputRange
                                          maxValue={100}
                                          minValue={0}
                                          value={effectsStates[index]}
                                          onChange={(range: any) => {
                                            handleRangeEffectChange(index, range);
                                          }}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="option" onClick={() => handleSelectOption("effects")}>
                              <div className="icon-container">
                                {currentOption === "effects" ? <CloseIcon /> : <EffectsStarsIcon className="icon-container__icon" />}
                              </div>
                              <span className="option-legend"><Trans>Effects</Trans></span>
                            </div>
                            <div className="arrow" onClick={() => handleSelectOption("effects")}>
                              <span aria-hidden="true" className={classnames("chq-dd--caret video-effects", { "chq-dd--caret-ex ": currentOption === "effects" })} />
                            </div>
                          </div>
                          <div
                            className={classnames(
                              "Options-Container__effects",
                              isDisableOption("prompter") ? "disabled" : "",
                              !isDisableOption("prompter") && !showScript ? "inactive" : "")}
                            onClick={() => {
                              if (!screenRecord) {
                                handleShowScriptChange(showScript ? "no" : "yes");
                              }
                            }}
                          >
                            <div className="option">
                              <div className="icon-container">
                                <PrompterIcon className="icon-container__icon" />
                              </div>
                              <span className="option-legend"><Trans>Prompter</Trans></span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
              {videoEditorOpen && (
                <GetVideoThumbnail
                  video={(screenRecord ? mediaBlobUrl : mediaBlob) || ""}
                  onEdit={handleVideoEdited}
                />
              )}
              {!screenRecord && <Loader loading={status !== "recording" && status !== "paused" && !videoEditorOpen} />}
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      <Button className={className} onClick={onOpen}>
        {children}
      </Button>
    </>
  );
};

export default EffectsSDKCameraRecorder;
